/* ============================================================================
   Search drawer UI. Logic lives in nwa-search.js (window.NWASearchCore); this
   file is only presentation and event wiring.
   ========================================================================== */
(function () {
  'use strict';

  var C = window.NWASearchCore;
  if (!C) return;

  var root, panel, input, body, foot, lastFocus;
  var timer = null;
  /* Every render is stamped with the sequence number of the keystroke that asked
     for it. Product and chip fetches resolve out of order, so a response whose
     stamp is no longer current is dropped rather than painted over newer text. */
  var seq = 0;

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Predictive search returns price as a decimal STRING already in the ACTIVE
     market currency ("14600.00"), not in cents. Verified against /products/x.js
     and the og:price meta on the same product.

     The currency is whatever market the shopper is in, so the symbol cannot be
     hardcoded: this store serves INR alongside USD, and a hardcoded "$" renders
     a 14,600 rupee tee as $14,600. Read the active currency off Shopify.currency
     and let Intl place the symbol and separators for it. */
  function money(value, currency) {
    var n = Number(value);
    if (!isFinite(n)) return '';
    /* Prefer the currencyCode that came with the amount — it cannot disagree
       with the number. Predictive search sends no code, so fall back to the
       active market currency there. */
    var cur = currency || (window.Shopify && window.Shopify.currency &&
               window.Shopify.currency.active) || 'USD';
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: cur, minimumFractionDigits: 2
      }).format(n);
    } catch (e) {
      return n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }
  }

  /* ---------- open / close ---------------------------------------------- */

  function open(seed) {
    if (!root.hidden) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.style.overflow = 'hidden';
    if (seed) { input.value = seed; }
    requestAnimationFrame(function () {
      root.classList.add('is-open');
      input.focus();
      /* Caret to the end so the customer carries on typing where they left off
         in the header field rather than in front of what they already typed. */
      try { input.setSelectionRange(input.value.length, input.value.length); } catch (e) {}
      if (seed) onInput();
    });
  }

  function close() {
    if (root.hidden) return;
    root.classList.remove('is-open');
    document.documentElement.style.overflow = '';
    window.setTimeout(function () {
      root.hidden = true;
      input.value = '';
      renderIdle();
    }, 180);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---------- rendering -------------------------------------------------- */

  function renderIdle() {
    body.innerHTML =
      '<div class="nwa-search__empty">' +
      '<p class="nwa-search__hint">Start typing a brand, category or product.</p>' +
      '</div>';
    /* :empty hides the footer bar, so clearing it removes the rule above it. */
    if (foot) foot.innerHTML = '';
  }

  function brandRowHtml(brand, extras) {
    var html =
      '<a class="nwa-search__brand" href="' + esc(C.brandUrl(brand)) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' + esc(brand.title) + '</span>' +
      '<span class="nwa-search__brand-meta">' + esc(brand.count || 0) + ' products</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a>';
    if (extras && extras.length) {
      html += '<div class="nwa-search__also">' +
        extras.map(function (b) {
          return '<a class="nwa-search__also-link" href="' + esc(C.brandUrl(b)) +
            '" data-nwa-item>' + esc(b.title) + '</a>';
        }).join('') + '</div>';
    }
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Brand</h2>' + html + '</section>';
  }

  function chipsHtml(brand, types) {
    if (!types || !types.length) return '';
    return '<section class="nwa-search__block" data-nwa-chips>' +
      '<h2 class="nwa-search__label">Shop ' + esc(brand.title) + ' by category</h2>' +
      '<div class="nwa-search__chips">' +
      types.map(function (t) {
        return '<a class="nwa-search__chip" href="' +
          esc(C.brandTypeUrl(brand, t.label)) + '" data-nwa-item>' +
          esc(t.label) + '<span class="nwa-search__chip-n">' + esc(t.count) + '</span></a>';
      }).join('') +
      '</div></section>';
  }

  /* Brands that sell this category. The mirror of the type chips, and the answer
     to a category-first query: "hoodie" should open up the brands that sell
     hoodies, not pick one of them. */
  function brandChipsHtml(cat, vendors) {
    if (!vendors || !vendors.length) return '';
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Shop ' + esc(cat.title) + ' by brand</h2>' +
      '<div class="nwa-search__chips">' +
      vendors.slice(0, 12).map(function (v) {
        return '<a class="nwa-search__chip" href="' +
          esc(C.allUrl({ type: cat.type, vendor: v.label })) + '" data-nwa-item>' +
          esc(v.label) + '<span class="nwa-search__chip-n">' + esc(v.count) + '</span></a>';
      }).join('') +
      '</div></section>';
  }

  /* Append the colour facet to an already-built collection URL. */
  function withColour(url, colour) {
    if (!colour) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + C.colourParam(colour);
  }

  /* Where a category row should land.

     Category alone -> the category collection, which is the natural landing page
     and has nothing to clear. Category PLUS a colour -> /collections/all carrying
     both as filters, so both show as removable chips. Landing on
     /collections/hoodies?<colour> would pin "hoodies" as the collection itself
     and leave the shopper able to clear only the colour. */
  function categoryDest(cat, colour) {
    if (!colour) return C.catUrl(cat);
    return C.allUrl({ type: cat.type, colour: colour });
  }

  function categoryHtml(cat, colour) {
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Category</h2>' +
      '<a class="nwa-search__brand" href="' +
      esc(categoryDest(cat, colour)) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' +
      esc(colour ? colour.title + ' ' + cat.title : cat.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a></section>';
  }

  function comboHtml(brand, cat, colour) {
    return '<section class="nwa-search__block">' +
      '<a class="nwa-search__combo" href="' +
      esc(withColour(C.brandTypeUrl(brand, cat.type), colour)) +
      '" data-nwa-item>' +
      '<span class="nwa-search__combo-text">' + esc(brand.title) + ' &middot; ' +
      esc(colour ? colour.title + ' ' + cat.title : cat.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">View &rsaquo;</span>' +
      '</a></section>';
  }

  function productsHtml(products, q, label) {
    if (!products.length) {
      return '<section class="nwa-search__block"><p class="nwa-search__none">' +
        'No products match &ldquo;' + esc(q) + '&rdquo;.</p></section>';
    }
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">' + esc(label || 'Products') + '</h2>' +
      '<div class="nwa-search__products">' +
      products.map(function (p) {
        var img = p.featured_image && p.featured_image.url;
        return '<a class="nwa-search__product" href="' + esc(p.url) + '" data-nwa-item>' +
          (img
            ? '<img class="nwa-search__product-img" src="' + esc(img) +
              '" alt="" loading="lazy" width="56" height="56">'
            : '<span class="nwa-search__product-img nwa-search__product-img--empty"></span>') +
          '<span class="nwa-search__product-text">' +
          '<span class="nwa-search__product-title">' + esc(p.title) + '</span>' +
          '<span class="nwa-search__product-meta">' +
          esc([p.vendor, p.type || p.product_type].filter(Boolean).join(' \u00b7 ')) + '</span>' +
          '</span>' +
          '<span class="nwa-search__product-price">' + esc(money(p.price, p.currency)) + '</span>' +
          '</a>';
      }).join('') +
      '</div></section>';
  }

  function footerHtml(q, opts) {
    return '<a class="nwa-search__all" data-nwa-all href="' +
      esc(C.searchUrl(q, opts)) + '" data-nwa-item>' +
      'See all results for &ldquo;' + esc(q) + '&rdquo;</a>';
  }

  /* ---------- the main render pass --------------------------------------- */

  function slot(name) { return body.querySelector('[data-nwa-' + name + '-slot]'); }
  function setChips(html) { var el = slot('chips'); if (el) el.innerHTML = html; }
  function setProducts(html) { var el = slot('products'); if (el) el.innerHTML = html; }
  function setAllHref(href) {
    var el = foot && foot.querySelector('[data-nwa-all]');
    if (el) el.setAttribute('href', href);
  }

  /* Storefront filter inputs for the facets we actually resolved. */
  function productFilters(cat, colour) {
    var f = [];
    if (cat) f.push({ productType: cat.type });
    if (colour) {
      f.push({ taxonomyMetafield: {
        namespace: 'shopify', key: 'color-pattern',
        value: 'gid://shopify/TaxonomyValue/' + colour.gid
      } });
    }
    return f;
  }

  function colourHtml(colour) {
    return '<section class="nwa-search__block">' +
      '<h2 class="nwa-search__label">Colour</h2>' +
      '<a class="nwa-search__brand" href="' +
      esc(C.allUrl({ colour: colour })) + '" data-nwa-item>' +
      '<span class="nwa-search__brand-name">' + esc(colour.title) + '</span>' +
      '<span class="nwa-search__brand-go" aria-hidden="true">Shop all &rsaquo;</span>' +
      '</a></section>';
  }

  function relevanceFallback(q, mine) {
    C.fetchProducts(q).then(function (ps) {
      if (mine === seq) setProducts(productsHtml(ps, q, 'Products'));
    });
  }

  function run(q) {
    var mine = ++seq;
    var parsed = C.parseQuery(q);
    var brand = parsed.brand;
    var cat = parsed.category;
    /* Words the entities did not account for — "black" in "black hoodies".
       Best sellers of the whole category would ignore them, so anything left
       over sends the product list back to text search. Entity rows and chips
       still show: the customer can still jump to Hoodies. */
    var colour = parsed.colour;
    var leftover = parsed.residual;

    /* Entity rows paint immediately — no awaiting the network. */
    var head = '';
    if (brand && cat) head += comboHtml(brand, cat, colour);
    if (brand) {
      var others = C.matchEntities(q, C.BRANDS).filter(function (b) {
        return b.handle !== brand.handle;
      }).slice(0, 3);
      head += brandRowHtml(brand, others);
    }
    if (!brand && cat) head += categoryHtml(cat, colour);

    body.innerHTML = head +
      '<div data-nwa-chips-slot></div>' +
      '<div data-nwa-products-slot><p class="nwa-search__loading">Searching&hellip;</p></div>';

    /* A brand filter belongs on the results link ONLY when a brand was typed.
       For a bare category the customer asked for hoodies, not one label's. */
    if (foot) {
      foot.innerHTML = footerHtml(q, (!brand && (cat || colour))
        ? { type: cat ? cat.type : null, colour: colour }
        : null);
    }

    /* ---- a brand was named: show what that brand sells ---- */
    if (brand) {
      C.fetchFacets(brand.handle).then(function (facets) {
        if (mine !== seq) return;
        setChips(chipsHtml(brand, facets.types));
        if (facets.vendor) {
          setAllHref(C.searchUrl(q, {
            vendor: facets.vendor,
            type: cat ? cat.type : null
          }));
        }
      });
      if (leftover) { relevanceFallback(q, mine); return; }
      C.fetchBestSellers(brand.handle, productFilters(cat, colour))
        .then(function (list) {
          if (mine !== seq) return;
          if (list.length) {
            setProducts(productsHtml(list, q,
              'Best selling ' + brand.title + (cat ? ' ' + cat.title : '')));
          } else {
            relevanceFallback(q, mine);
          }
        });
      return;
    }

    /* ---- only a category: open up the BRANDS that sell it ---- */
    if (cat) {
      C.fetchFacets(cat.handle).then(function (facets) {
        if (mine !== seq) return;
        setChips(brandChipsHtml(cat, facets.vendors));
      });
      if (leftover) { relevanceFallback(q, mine); return; }
      C.fetchBestSellers(cat.handle, productFilters(null, colour)).then(function (list) {
        if (mine !== seq) return;
        if (list.length) setProducts(productsHtml(list, q, 'Best selling ' + cat.title));
        else relevanceFallback(q, mine);
      });
      return;
    }

    /* ---- only a colour ---- */
    if (colour) {
      setChips('');
      body.insertAdjacentHTML('afterbegin', colourHtml(colour));
      if (foot) foot.innerHTML = footerHtml(q, null);
      relevanceFallback(q, mine);
      return;
    }

    /* ---- nothing resolved: relevance, then try to name the brand ---- */
    C.fetchProducts(q).then(function (products) {
      if (mine !== seq) return;
      setProducts(productsHtml(products, q, 'Products'));
      inferBrand(products, q, mine);
    });
  }

  /* Back-inference runs ONLY when neither a brand nor a category resolved.
     It exists for misspelled brands — "spider" returns Sp5der products, so the
     brand can be named from the vendor of the results. Shopify's own semantic
     matching is what relates the two, so there is nothing to configure.
     It must never run on a category query: "hoodie"
     would pick whichever label happens to top the results and wrongly present
     the whole category as that one brand. */
  function inferBrand(products, q, mine) {
    if (!products.length) return;
    var counts = {};
    products.forEach(function (p) {
      if (p.vendor) counts[p.vendor] = (counts[p.vendor] || 0) + 1;
    });
    var top = Object.keys(counts).sort(function (a, b) {
      return counts[b] - counts[a];
    })[0];
    /* A bare majority is not evidence. "black hoodies" used to land here and
       promote whichever label happened to hold three of six results. Demand near
       unanimity, which is what a genuinely misspelled brand produces — "spider"
       returns Sp5der and nothing else. */
    if (products.length < 3) return;
    if (!top || counts[top] < Math.ceil(products.length * 0.8)) return;
    var hit = C.matchEntities(top, C.BRANDS, { whole: true })[0];
    if (!hit || mine !== seq) return;
    body.insertAdjacentHTML('afterbegin', brandRowHtml(hit, []));
    C.fetchFacets(hit.handle).then(function (facets) {
      if (mine !== seq) return;
      setChips(chipsHtml(hit, facets.types));
      if (facets.vendor) setAllHref(C.searchUrl(q, { vendor: facets.vendor }));
    });
  }

  /* ---------- events ------------------------------------------------------ */

  function onInput() {
    var q = input.value.trim();
    window.clearTimeout(timer);
    if (q.length < C.MIN_CHARS) { seq++; renderIdle(); return; }
    timer = window.setTimeout(function () { run(q); }, C.DEBOUNCE);
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); return; }
    var items = Array.prototype.slice.call(
      panel.querySelectorAll('[data-nwa-item]'));
    if (!items.length) return;
    var i = items.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      (items[i + 1] || items[0]).focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (i <= 0) input.focus(); else items[i - 1].focus();
    }
  }

  function onSubmit(e) {
    if (e.key !== 'Enter' || document.activeElement !== input) return;
    var q = input.value.trim();
    if (!q) return;
    e.preventDefault();
    /* Enter goes wherever "See all results" points, rather than rebuilding the
       URL. That link already carries whatever facets resolved, so the two cannot
       disagree — before this, Enter gave the raw OR-matched page (1,048 results
       for "black hoodies") while the button gave the narrowed one.

       Still the results page, never a guessed destination: the entity rows are
       right there if the customer wanted one of those instead. */
    var all = foot && foot.querySelector('[data-nwa-all]');
    window.location.href = (all && all.getAttribute('href')) || C.searchUrl(q);
  }

  function init() {
    root = document.getElementById('nwa-search');
    if (!root) return;
    panel = root.querySelector('.nwa-search__panel');
    input = document.getElementById('nwa-search-input');
    body = document.getElementById('nwa-search-body');
    foot = document.getElementById('nwa-search-foot');

    document.addEventListener('click', function (e) {
      /* Text nodes and the document itself have no closest(); guard before use. */
      if (!e.target || typeof e.target.closest !== 'function') return;
      var trigger = e.target.closest('[data-nwa-search-trigger], .nwa-search-open');
      if (trigger) {
        e.preventDefault();
        /* The desktop header keeps a real <input name="q"> so the page still
           works without JS. When it does have JS, hand whatever is in it to the
           drawer instead of throwing the keystrokes away. */
        /* The trigger may BE the field (results page) or merely contain one
           (header form), so handle both rather than assuming a wrapper. */
        var field = (trigger.matches && trigger.matches('input[name="q"]'))
          ? trigger
          : (trigger.querySelector && trigger.querySelector('input[name="q"]'));
        open(field && field.value ? field.value.trim() : '');
        return;
      }
      if (e.target.closest('[data-nwa-close]')) { e.preventDefault(); close(); }
    });

    /* Focus, not just click: tabbing into a search field should open the drawer
       too, or keyboard users get a dead input on the results page. */
    document.addEventListener('focusin', function (e) {
      var t = e.target;
      if (!t || !t.matches || !t.matches('[data-nwa-search-trigger]')) return;
      if (t === input || !root.hidden) return;
      open(t.value ? t.value.trim() : '');
    });

    input.addEventListener('input', onInput);
    input.addEventListener('keydown', onSubmit);
    root.addEventListener('keydown', onKeydown);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
